import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import WidgetApiService from '@/common/services/widget-api.service';
import useTransactionsStore from '@/store/modules/transactions';
import router from '@/router';
import { AuthTypes } from '@/types/auth';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
import { TransactTypes } from '@/types/transact';
import { CountryTypes } from '@/types/country';
import { KycTypes } from '@/types/kyc';
import { getCountryCodeFromCountry, validateUrl } from '@/utils/helpers';
import mixpanel from '@/utils/mixpanel';
import { useKycStore } from './kyc';
import { useGeneralStore } from './general';
var SupportedCountriesConfig = CountryTypes.SupportedCountriesConfig;
export const useAuthStore = defineStore('auth', () => {
    const kycStore = useKycStore();
    const { statesCities, issueKCC, didUri, requireDoubleIdNG } = storeToRefs(kycStore);
    const transactionsStore = useTransactionsStore();
    const generalStore = useGeneralStore();
    const { widgetFlowsEnabled } = storeToRefs(generalStore);
    const sellEnabled = ref(false);
    const travelRuleConfig = ref();
    const currentStep = ref(AuthTypes.AuthSteps.AUTH_1);
    const email = ref('');
    const session = ref('');
    const user = ref();
    const token = ref('');
    const refreshToken = ref('');
    const isLoggedIn = ref(false);
    const tokenExpiry = ref(0);
    const standaloneAuthFlow = ref(false);
    const partnerVerified = ref(false);
    const partnerSessionId = ref('');
    const partnerSessionTimer = ref();
    const deviceId = ref();
    const partnerKey = ref('');
    const partnerId = ref('');
    const partnerRedirectEnabled = ref(false);
    const partnerRedirectLink = ref('');
    const userNotRegistered = ref(false);
    const sellRedirectLink = ref(undefined);
    function resetStore() {
        email.value = '';
        session.value = '';
        user.value = undefined;
        token.value = '';
        refreshToken.value = '';
        isLoggedIn.value = false;
        tokenExpiry.value = 0;
        currentStep.value = AuthTypes.AuthSteps.AUTH_1;
    }
    function resetUserAndPartnerStore() {
        resetStore();
        partnerVerified.value = false;
        partnerSessionId.value = '';
        partnerSessionTimer.value = null;
        partnerKey.value = '';
        partnerId.value = '';
        partnerRedirectEnabled.value = false;
        partnerRedirectLink.value = '';
    }
    function setPartnerSellRedirectLink(redirectLink) {
        sellRedirectLink.value = redirectLink;
    }
    function setUserState(state) {
        user.value = state;
        if (user.value.didUri && didUri.value && user.value.didUri === didUri.value)
            issueKCC.value = true;
    }
    function setFromQueryParams(query) {
        email.value = query.email ?? '';
        if (!query.redirectLink)
            return;
        // This must only be set if it is in the query parameters, otherwise it will
        // overwrite what is in the partner config
        const validUrl = validateUrl(query.redirectLink);
        if (!validUrl) {
            console.error('redirect url passed in query param is invalid');
            throw new Error('redirect url passed in query param is invalid');
        }
        if (query.txType === TransactTypes.TransactionType.BUY) {
            partnerRedirectLink.value = query.redirectLink;
            partnerRedirectEnabled.value = true;
        }
        else if (query.txType === TransactTypes.TransactionType.SELL) {
            setPartnerSellRedirectLink(query.redirectLink);
        }
    }
    function setPersonalDetails(firstname, lastname, dob, mobileNumber) {
        if (!user.value) {
            throw new Error('cannot proceed with kyc without user object');
        }
        user.value.firstName = firstname;
        user.value.lastName = lastname;
        user.value.dob = dob;
        user.value.phone = mobileNumber;
    }
    function setResidentialDetails(address) {
        if (!user.value) {
            throw new Error('cannot proceed with kyc without user object');
        }
        user.value.address = address;
        user.value.country = address.country;
    }
    async function CheckAccessToken() {
        try {
            const { accessToken, expiry } = await WidgetApiService.RefreshAccessToken({ refreshToken: refreshToken.value }, partnerSessionId.value, deviceId.value);
            token.value = accessToken;
            tokenExpiry.value += expiry - 50;
            return accessToken;
        }
        catch (e) {
            // need to log user out
            transactionsStore.resetStore();
            resetStore();
            const { currentStep: transactStep } = storeToRefs(transactionsStore);
            transactStep.value = TransactTypes.TransactSteps.BUY_1;
            router.push('/transact');
            console.error('token refresh failed');
            throw new Error('invalid token');
        }
    }
    async function SignIn(input) {
        try {
            const { session: newSession } = await WidgetApiService.SignIn({
                ...input,
            }, partnerSessionId.value, deviceId.value);
            session.value = newSession;
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
    }
    function setSession(sessionId) {
        partnerSessionId.value = sessionId;
        partnerVerified.value = true;
    }
    function setPartnerRedirectConfig(redirectEnabled, redirectLink) {
        if (redirectEnabled)
            partnerRedirectEnabled.value = redirectEnabled;
        if (redirectLink)
            partnerRedirectLink.value = redirectLink;
    }
    function DetermineAndSetPartnerAccess(featureFlags) {
        featureFlags.forEach(flag => {
            switch (flag) {
                case WidgetApiServiceTypes.FeatureFlags.WIDGET_SELL:
                    sellEnabled.value = true;
                    break;
                default:
                    break;
            }
        });
    }
    async function VerifyApiKey(params) {
        const { apiKey, redirectLinkSignature } = params;
        try {
            const { sessionId: partnerSession, redirectEnabled, collectionRedirectLink, partnerId: id, featureFlags, country, countries, widgetFlowsEnabled: partnerWidgetFlows, } = await WidgetApiService.VerifyApiKey({
                apiKey,
                deviceId: deviceId.value,
                redirectLink: params.redirectLink,
                redirectLinkSignature,
            });
            partnerKey.value = apiKey;
            partnerId.value = id;
            if (partnerWidgetFlows.length) {
                widgetFlowsEnabled.value = partnerWidgetFlows;
            }
            if (featureFlags?.length)
                DetermineAndSetPartnerAccess(featureFlags);
            requireDoubleIdNG.value =
                country.country === CountryTypes.CountryCodes.NIGERIA;
            setSession(partnerSession);
            const redirectLink = validateUrl(collectionRedirectLink)
                ? collectionRedirectLink
                : '';
            setPartnerRedirectConfig(redirectEnabled, redirectLink);
            transactionsStore.countryConfigs = countries ?? [];
            transactionsStore.setSelectedCurrency({
                currency: country.currency,
                countryCode: country.country,
            });
            const activeConfig = SupportedCountriesConfig[transactionsStore.countryConfig.country];
            if (!activeConfig) {
                console.error(`country ${transactionsStore.countryConfig.country} not implemented`);
                throw new Error(`country ${transactionsStore.countryConfig.country} not implemented`);
            }
            transactionsStore.activeSupportedCountryConfig = activeConfig;
        }
        catch (e) {
            throw new Error('API Key verification failure, cannot proceed');
        }
    }
    async function Logout(partnerLogout = false) {
        try {
            await WidgetApiService.Logout(token.value, partnerSessionId.value, deviceId.value);
            if (!partnerLogout)
                await VerifyApiKey({ apiKey: partnerKey.value });
            mixpanel.reset();
        }
        catch (e) {
            console.error(`failed to logout user :${e.message}`);
        }
    }
    async function SignUp(input) {
        try {
            await WidgetApiService.SignUp(input, partnerSessionId.value, deviceId.value);
            userNotRegistered.value = false;
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
    }
    async function GetCountryStates() {
        try {
            const data = await WidgetApiService.GetCountryStates(token.value, partnerSessionId.value, deviceId.value, transactionsStore.countryConfig.country);
            // sort response by state name
            data.sort((a, b) => {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });
            statesCities.value.splice(0);
            statesCities.value.push(...data);
        }
        catch (e) {
            throw new Error('failed to get states for country');
        }
    }
    async function getTravelRuleConfig() {
        try {
            const data = await WidgetApiService.GetTravelRuleConfig(token.value, partnerSessionId.value, deviceId.value, transactionsStore.countryConfig.country);
            travelRuleConfig.value = data;
        }
        catch {
            throw new Error('error while getting travel rule config');
        }
    }
    async function SubmitCode(input) {
        try {
            const { user: newUser, token: newToken, refreshToken: newRefreshToken, session: newSession, } = await WidgetApiService.SubmitCode({ ...input }, partnerSessionId.value, deviceId.value);
            if (newUser && newToken && newRefreshToken) {
                token.value = newToken;
                refreshToken.value = newRefreshToken;
                setUserState(newUser);
                clearTimeout(partnerSessionTimer.value);
                kycStore.setKycState(newUser.kycStatus);
                tokenExpiry.value = Math.floor((Date.now() + 4 * 60 * 1000) / 1000);
                isLoggedIn.value = true;
                mixpanel.identify(newUser.id);
                if (newUser.kycStatus !== KycTypes.KycStatus.INCOMPLETE &&
                    newUser.country) {
                    const countryCode = getCountryCodeFromCountry(newUser.country);
                    const config = SupportedCountriesConfig[countryCode];
                    transactionsStore.setSelectedCurrency({
                        currency: config.currency,
                        countryCode,
                    });
                }
                const postLoginRequests = [
                    WidgetApiService.GetTierLimit(newToken, 1, transactionsStore.countryConfig.country, partnerSessionId.value, deviceId.value),
                    getTravelRuleConfig(),
                ];
                if (newUser.kycStatus !== KycTypes.KycStatus.APPROVED) {
                    postLoginRequests.push(GetCountryStates());
                }
                const [limit] = await Promise.allSettled(postLoginRequests);
                if (limit.status === 'fulfilled') {
                    user.value.tierLimit = limit.value;
                }
            }
            if (newSession) {
                session.value = newSession;
            }
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
    }
    return {
        currentStep,
        email,
        session,
        SignIn,
        SignUp,
        SubmitCode,
        isLoggedIn,
        token,
        refreshToken,
        user,
        setPersonalDetails,
        setResidentialDetails,
        setFromQueryParams,
        standaloneAuthFlow,
        setUserState,
        CheckAccessToken,
        partnerVerified,
        VerifyApiKey,
        deviceId,
        partnerSessionId,
        resetStore,
        partnerKey,
        partnerId,
        Logout,
        partnerRedirectEnabled,
        partnerRedirectLink,
        resetUserAndPartnerStore,
        sellEnabled,
        userNotRegistered,
        GetCountryStates,
        sellRedirectLink,
        travelRuleConfig,
    };
});
export default useAuthStore;
